import React, { useEffect, useMemo, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Container,
  Fab,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PageWrapper from "../../PageWrapper"
import FlightActionsSheet from "./FlightActionsSheet"
import FlightCard from "./FlightCard"
import FlightFormDialog from "./FlightFormDialog"
import { useFlights } from "./useFlights"
import { partitionFlights, todayLocal } from "utils/flightLogic"

function FlightsPage() {
  const {
    flights,
    loading,
    error,
    clearError,
    addFlight,
    updateFlight,
    deleteFlight,
  } = useFlights()

  const [today, setToday] = useState(todayLocal)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [menuFlight, setMenuFlight] = useState(null)

  // The kiosk stays open for days. Without this, a departed flight would sit in
  // Upcoming until someone reloaded the page.
  useEffect(() => {
    const tick = () => setToday(todayLocal())
    const interval = setInterval(tick, 60000)
    document.addEventListener("visibilitychange", tick)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", tick)
    }
  }, [])

  const { upcoming, past } = useMemo(
    () => partitionFlights(flights, today),
    [flights, today]
  )

  const openAdd = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (flight) => {
    setMenuFlight(null)
    setEditing(flight)
    setFormOpen(true)
  }

  const handleSubmit = (values) => {
    if (editing) {
      updateFlight(editing.id, values)
    } else {
      addFlight(values)
    }
  }

  return (
    <PageWrapper title="Flight Tracker">
      <Container
        maxWidth="sm"
        sx={{
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 11, sm: 12 },
        }}
      >
        {/* color="inherit" is load-bearing: _style.scss colors bare h1-h6
            elements dark navy, which the MuiTypography-h4 class does not
            override, leaving the title invisible on the dark shell. */}
        <Typography
          variant="h4"
          color="inherit"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.6rem", sm: "2.125rem" },
            mb: 0.25,
          }}
        >
          Flight Tracker
        </Typography>
        <Typography
          variant="body2"
          sx={{ opacity: 0.65, mb: { xs: 2, sm: 3 } }}
        >
          Tap a flight to check its status on Google.
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* !error keeps "no flights" from contradicting the error Snackbar: a
            failed read also leaves flights empty. */}
        {!loading && !error && flights.length === 0 && (
          <Box sx={{ textAlign: "center", py: { xs: 5, sm: 8 }, opacity: 0.6 }}>
            <Typography>No flights yet.</Typography>
            <Typography variant="body2">Tap + to add one.</Typography>
          </Box>
        )}

        {upcoming.length > 0 && (
          <>
            <Typography
              variant="overline"
              sx={{ opacity: 0.6, letterSpacing: 1.5, display: "block", mb: 1 }}
            >
              Upcoming
            </Typography>
            <Stack spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: { xs: 2.5, sm: 4 } }}>
              {upcoming.map((flight) => (
                <FlightCard
                  key={flight.id}
                  flight={flight}
                  today={today}
                  onMenu={setMenuFlight}
                />
              ))}
            </Stack>
          </>
        )}

        {past.length > 0 && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{ background: "transparent", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.6, letterSpacing: 1.5 }}
              >
                Past flights ({past.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <Stack spacing={1}>
                {past.map((flight) => (
                  <FlightCard
                    key={flight.id}
                    flight={flight}
                    today={today}
                    past
                    onMenu={setMenuFlight}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Container>

      <Fab
        color="primary"
        aria-label="Add flight"
        onClick={openAdd}
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
        }}
      >
        <AddIcon />
      </Fab>

      <FlightFormDialog
        open={formOpen}
        initialFlight={editing}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <FlightActionsSheet
        flight={menuFlight}
        onClose={() => setMenuFlight(null)}
        onEdit={openEdit}
        onDelete={(flight) => deleteFlight(flight.id)}
      />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={clearError} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </PageWrapper>
  )
}

export default FlightsPage
